import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LogoWithText } from './common/Logo';
import {
  Home,
  Brain,
  MessageCircle,
  User,
  Menu,
  X,
  LogOut,
  Tag,
  FileText,
  Settings,
  Lightbulb,
  Newspaper
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, isAdminUser, signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navigation = [
    { name: '首页', href: '/', icon: Home },
    { name: '热点演练', href: '/training', icon: Brain },
    { name: '问题诊断', href: '/problem-diagnosis', icon: Lightbulb },
    { name: 'AI助手', href: '/ai-assistant', icon: MessageCircle },
    { name: '用户中心', href: '/profile', icon: User },
  ];

  const adminNavigation = [
    { name: '管理中心', href: '/admin', icon: Settings }
  ];

  const isActive = (href: string) => {
    return location.pathname === href;
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      // 退出成功后导航到首页
      navigate('/');
    } catch (error) {
      console.error('退出登录失败:', error);
      // 即使出错，也导航到首页，因为可能只是session已过期
      // 用户界面会通过AuthContext的状态变化自动更新
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 relative">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50 safe-area-inset-top">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-3 rounded-lg bg-white shadow-lg text-gray-600 hover:text-gray-900 touch-manipulation"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      } safe-area-inset-left`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 px-4 border-b border-gray-200 safe-area-inset-top">
            <LogoWithText size="md" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto pb-safe">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors touch-manipulation ${
                    isActive(item.href)
                      ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}

            {isAdminUser && (
              <>
                <div className="border-t border-gray-200 my-4"></div>
                {adminNavigation.map((item) => {
                  const Icon = item.icon;
                  const isAdminActive = location.pathname.startsWith('/admin');
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors touch-manipulation ${
                        isAdminActive
                          ? 'bg-red-50 text-red-600 border-r-2 border-red-600'
                          : 'text-gray-600 hover:bg-red-50 hover:text-red-600'
                      }`}
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      {item.name}
                    </Link>
                  );
                })}
              </>
            )}
          </nav>

          {/* User info and logout */}
          {user && (
            <div className="border-t border-gray-200 p-4 pb-safe">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user.email}
                  </p>
                  {isAdminUser && (
                    <p className="text-xs text-blue-600">管理员</p>
                  )}
                </div>
              </div>
              <button
                onClick={handleSignOut}
                className="flex items-center w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors touch-manipulation"
              >
                <LogOut className="w-4 h-4 mr-2" />
                退出登录
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 pt-16 lg:pt-0">
        <main className="min-h-screen pb-safe">
          {children}
        </main>
      </div>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;